import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import AboutUs from "../views/AboutUs.vue";
import Faq from "../views/Faq.vue";
import Contact from "../views/Contact.vue";
import Login from "../views/Login.vue";
import Register from "../views/Register.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import ResetPassword from "../views/ResetPassword.vue";
import VerifyAccount from "../views/VerifyAccount.vue";
import OAuth from "../views/OAuth.vue";
import { hasRole, hasPermission, hasCompanyPermission } from "@/helpers/auth";

const guest = (to, from, next) => {
  if (!store.getters["auth/authenticated"]) {
    return next();
  } else {
    return next({ name: "jobs" });
  }
};

const auth = (to, from, next) => {
  if (store.getters["auth/authenticated"]) {
    return next();
  } else {
    return next({ name: "login" });
  }
};

const hasAccessRoles = (to, from, next) => {
  const roles = store.getters["auth/userRoles"];
  if (
    hasRole(roles, [
      "Super Admin",
      "Admin",
      "Audit",
      "Accounting",
      "Audit Viewer",
      "Accounting Viewer",
      "IT",
      "TimeKeeper",
      "FieldKeeper",
      "Project Engineer",
      "Project Manager",
      "Construction Head",
    ])
  ) {
    return next();
  } else {
    return next({ name: "jobs" });
  }
};

const isEmployee = (to, from, next) => {
  const roles = store.getters["auth/userRoles"];
  if (hasRole(roles, ["Admin", "Employee"])) {
    return next();
  } else {
    return next({ name: "jobs" });
  }
};

const isHead = (to, from, next) => {
  const userHeadDepartments = store.getters["auth/userHeadDepartments"];
  if (userHeadDepartments.length > 0) {
    return next();
  } else {
    return next({ name: "jobs" });
  }
};

const isHeadOrAdmin = (to, from, next) => {
  const userHeadDepartments = store.getters["auth/userHeadDepartments"];
  const roles = store.getters["auth/userRoles"];
  if (userHeadDepartments.length > 0 || hasRole(roles, ["Admin"])) {
    return next();
  } else {
    return next({ name: "jobs" });
  }
};

const hasPermissionAccess = (permission) => (to, from, next) => {
  const permissions = store.getters["auth/userPermissions"];
  const roles = store.getters["auth/userRoles"];
  const companySettings = store.getters["auth/companySettings"];
  if (hasRole(roles, "") || hasCompanyPermission(permissions, roles, companySettings, permission)) {
    return next();
  } else {
    return next({ name: "jobs" });
  }
};

const hasAnyPermissionAccess = (permissions) => (to, from, next) => {
  const userPermissions = store.getters["auth/userPermissions"];
  const userRoles = store.getters["auth/userRoles"];
  if (hasRole(userRoles, "") || hasPermission(userPermissions, permissions)) {
    return next();
  }
  return next({ name: "jobs" });
};

const routes = [
  {
    path: "/jobs",
    alias: "/",
    name: "jobs",
    component: () => import(/* webpackChunkName: "jobs" */ "../views/Jobs.vue"),
  },
  {
    path: "/about-us",
    name: "aboutUs",
    component: AboutUs,
  },
  {
    path: "/faq",
    name: "faq",
    component: Faq,
  },
  {
    path: "/contact",
    name: "contact",
    component: Contact,
  },
  {
    path: "/login",
    name: "login",
    component: Login,
    beforeEnter: guest,
  },
  {
    path: "/register",
    name: "register",
    component: Register,
    beforeEnter: guest,
  },
  {
    path: "/forgot-password",
    name: "forgotPassword",
    component: ForgotPassword,
    beforeEnter: guest,
  },
  {
    path: "/reset-password",
    name: "resetPassword",
    component: ResetPassword,
    beforeEnter: [
      guest,
      (to, from, next) => {
        if (!to.query.token || !to.query.email) {
          next({ name: "login" });
        } else {
          next();
        }
      },
    ],
  },
  {
    path: "/verify-account/:id",
    name: "verifyAccount",
    component: VerifyAccount,
    beforeEnter: guest,
  },
  {
    path: "/oauth/:id",
    component: OAuth,
    beforeEnter: guest,
    meta: {
      hideNavbar: true,
      hideFooter: true,
    },
  },
  {
    path: "/jobs/:id",
    name: "viewJob",
    // route level code-splitting
    // this generates a separate chunk (viewJob.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "viewJob" */ "../views/job/View.vue"),
    beforeEnter: auth,
  },
  {
    path: "/account",
    name: "account",
    // route level code-splitting
    // this generates a separate chunk (account.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/Account.vue"),
    beforeEnter: auth,
  },
  {
    path: "/profile",
    name: "profile",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/account/Profile.vue"),
    beforeEnter: auth,
  },
  {
    path: "/job-applications",
    name: "jobApplications",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/JobApplications.vue"
      ),
    beforeEnter: auth,
  },
  {
    path: "/attendance",
    name: "attendance",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/Attendance.vue"
      ),
    beforeEnter: isEmployee,
  },
  {
    path: "/department/attendance",
    name: "DepartmentAttendance",
    component: () => import("../views/department-head/Attendance.vue"),
    beforeEnter: isHead,
  },
  {
    path: "/my-leaves",
    name: "myLeaves",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/account/MyLeaves.vue"),
    beforeEnter: isEmployee,
  },
  {
    path: "/my-overtime",
    name: "myOvertime",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/MyOvertime.vue"
      ),
    beforeEnter: isEmployee,
  },
  {
    path: "/salary_request",
    name: "RequestSalary",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/RequestSalaryIncrease.vue"
      ),
    beforeEnter: isEmployee,
  },
  {
    path: "/my-official-business",
    name: "myOfficialBusiness",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/MyOfficialBusiness.vue"
      ),
    beforeEnter: isEmployee,
  },
  {
    path: "/my-docs",
    name: "myDocs",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/account/MyDocs.vue"),
    beforeEnter: isEmployee,
  },
  {
    path: "/my-payroll",
    name: "myPayroll",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/MyPayroll.vue"
      ),
    beforeEnter: isEmployee,
  },
  {
    path: "/my-loans",
    name: "myLoan",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/account/MyLoans.vue"),
    beforeEnter: isEmployee,
  },
  {
    path: "/approval-forms",
    name: "approvalForms",
    component: () =>
      import(
        /* webpackChunkName: "account" */ "../views/account/ApprovalForms.vue"
      ),
    beforeEnter: [isEmployee, isHeadOrAdmin],
  },
  {
    path: "/admin/dashboard",
    name: "dashboard",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Dashboard.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Dashboard")],
    meta: {
      adminView: true,
      title: "Dashboard",
    },
  },
  {
    path: "/admin/companies",
    name: "companies",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Companies.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Companies")],
    meta: {
      adminView: true,
      title: "Companies",
    },
  },
  {
    path: "/admin/company-schedule",
    name: "companies-schedules",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/CompanySchedule.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Company Schedules"),
    ],
    meta: {
      adminView: true,
      title: "Company Schedules",
    },
  },
  {
    path: "/admin/shifts",
    name: "shifts",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Shifts.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Shifts")],
    meta: {
      adminView: true,
      title: "Shifts",
    },
  },
  {
    path: "/admin/departments",
    name: "departments",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Departments.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Departments")],
    meta: {
      adminView: true,
      title: "Departments",
    },
  },
  {
    path: "/admin/department-roles",
    name: "department-roles",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/DepartmentRoles.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Department Roles"),
    ],
    meta: {
      adminView: true,
      title: "Department Roles",
    },
  },
  {
    path: "/admin/groups",
    name: "groups",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Groups.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Groups")],
    meta: {
      adminView: true,
      title: "Groups",
    },
  },
  {
    path: "/admin/attendances",
    name: "attendances",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Attendances.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Attendances")],
    meta: {
      adminView: true,
      title: "Attendances",
    },
  },
  {
    path: "/admin/activity-logs",
    name: "activity-logs",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/ActivityLogs.vue"),
    beforeEnter: [
      hasAnyPermissionAccess(["Access Activity Logs"]),
    ],
    meta: {
      adminView: true,
      title: "Activity Logs",
    },
  },
  {
    path: "/admin/overtime-approvals",
    name: "overtime-approvals",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/OvertimeApprovals.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess([
        "Access Overtime Approvals",
        "Validate Overtime",
        "Approve Overtime (PE)",
        "Approve Overtime (PM)",
        "Approve Overtime (CH)",
      ]),
    ],
    meta: {
      adminView: true,
      title: "Overtime Approvals",
    },
  },
  {
    path: "/admin/attendance-verifications",
    name: "attendance-verifications",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/AttendanceVerifications.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Attendance Verifications"),
    ],
    meta: {
      adminView: true,
      title: "Attendance Verifications",
    },
  },
  {
    path: "/admin/attendance-resolutions",
    name: "attendance-resolutions",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/AttendanceResolutions.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess([
        "Access Attendance Resolutions",
        "Request Attendance Resolution",
        "Approve Attendance Resolution",
      ]),
    ],
    meta: {
      adminView: true,
      title: "Attendance Resolutions",
    },
  },
  {
    path: "/admin/employee-creations",
    name: "employee-creations",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/EmployeeCreations.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess([
        "Access Employee Creations",
        "Request Employee Creation",
        "Approve Employee Creation (PM)",
        "Approve Employee Creation (CH)",
        "Approve Employee Creation (Approver 1)",
        "Validate Employee Creation (Audit)",
        "Validate Employee Creation (Accounting)",
      ]),
    ],
    meta: {
      adminView: true,
      title: "Employee Creations",
    },
  },
  {
    path: "/admin/attendance-requests",
    name: "attendance-requests",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/AttendanceRequest.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Attendance Requests"),
    ],
    meta: {
      adminView: true,
      title: "Attendance Requests",
    },
  },
  {
    path: "/admin/permissions",
    name: "permissions",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Permissions.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Permissions")],
    meta: {
      adminView: true,
      title: "Permissions",
    },
  },
  {
    path: "/admin/roles",
    name: "roles",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Roles.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Roles")],
    meta: {
      adminView: true,
      title: "Roles",
    },
  },
  {
    path: "/admin/payrolls",
    name: "payrolls",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Payrolls.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Payrolls")],
    meta: {
      adminView: true,
      title: "Payrolls",
    },
  },
  {
    path: "/admin/payrolls/:id",
    name: "PayrollDetails",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/PayrollDetails.vue"
      ),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Payrolls")],
    meta: {
      adminView: true,
      title: "Payroll Details",
    },
  },
  {
    path: "/admin/claim-salary",
    name: "claim-salary",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/SalaryClaim.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Claim Salary")],
    meta: {
      adminView: true,
      title: "Claim Salary",
    },
  },
  {
    path: "/admin/salary_increase",
    name: "salary_increase",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/SalaryIncrease.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Salary Increase"),
    ],
    meta: {
      adminView: true,
      title: "Salary Increase",
    },
  },
  {
    path: "/admin/salary-change-requests",
    name: "salary-change-requests",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/SalaryChangeRequests.vue"
      ),
    // No separate "access" permission: the requesters file a change and follow it up,
    // Accounting approves it first and Audit gives the final approval.
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess([
        "Access Salary Detail Change Requests",
        "Request Salary Detail Change",
        "Approve Salary Detail Change (Accounting)",
        "Approve Salary Detail Change (Audit)",
      ]),
    ],
    meta: {
      adminView: true,
      title: "Salary Change Requests",
    },
  },
  {
    path: "/admin/loans",
    name: "loans",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/loans/index.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Loans")],
    meta: {
      adminView: true,
      title: "Loan",
    },
  },
  {
    path: "/loans/:id",
    name: "loan-details",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/loans/LoanDetails.vue"
      ),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Loans")],
    meta: {
      adminView: true,
      title: "Loan Details",
    },
  },
  {
    path: "/admin/loan-types",
    name: "loan-types",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/LoanTypes.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Loan Types")],
    meta: {
      adminView: true,
      title: "Loan Types",
    },
  },
  {
    path: "/admin/schedule",
    name: "schedules",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Schedule.vue"),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Employee Schedules"),
    ],
    meta: {
      adminView: true,
      title: "Schedule",
    },
  },
  {
    path: "/admin/holidays",
    name: "holidays",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Holidays.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Holidays")],
    meta: {
      adminView: true,
      title: "Holidays",
    },
  },
  {
    path: "/admin/admins",
    name: "admins",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Admins.vue"),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess(["Access Admins", "Create Accounts"]),
    ],
    meta: {
      adminView: true,
      title: "Admins",
    },
  },
  {
    path: "/admin/employees",
    name: "employees",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/employees/index.vue"
      ),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Employees")],
    meta: {
      adminView: true,
      title: "Employees",
    },
  },
  {
    path: "/admin/employee/:id",
    name: "view employee",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/employees/[id].vue"
      ),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Employees")],
    meta: {
      adminView: true,
      title: "Employee's 201",
    },
  },
  {
    path: "/admin/applicants",
    name: "applicants",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Applicants.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Applicants")],
    meta: {
      adminView: true,
      title: "Applicants",
    },
  },
  {
    path: "/admin/jobs",
    name: "adminJobs",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Jobs.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Jobs")],
    meta: {
      adminView: true,
      title: "Jobs",
    },
  },
  {
    path: "/admin/applications",
    name: "applications",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/admin/Applications.vue"),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Applications")],
    meta: {
      adminView: true,
      title: "Applications",
    },
  },
  {
    path: "/admin/qualifications",
    name: "qualifications",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/Qualifications.vue"
      ),
    beforeEnter: [hasAccessRoles, hasPermissionAccess("Access Qualifications")],
    meta: {
      adminView: true,
      title: "Qualifications",
    },
  },
  {
    path: "/admin/responsibilities",
    name: "responsibilities",
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/admin/Responsibilities.vue"
      ),
    beforeEnter: [
      hasAccessRoles,
      hasPermissionAccess("Access Responsibilities"),
    ],
    meta: {
      adminView: true,
      title: "Responsibilities",
    },
  },
  {
    path: "/:catchAll(.*)*",
    name: "pageNotFound",
    component: () =>
      import(
        /* webpackChunkName: "pageNotFound" */ "../views/PageNotFound.vue"
      ),
  },
  {
    path: "/facetime-attendance",
    alias: "/",
    name: "faceRecognition",
    component: () => import("@/views/FaceRecognition"),
    meta: {
      title: "FaceRecognition",
      hideFooter: true,
    },
  },
  {
    path: "/attendance/clock-in",
    alias: "/",
    component: () => import("@/views/attendance/ClockIn"),
    meta: {
      title: "FaceRecognition",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

export default router;
